import Head from 'next/head';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { useAppSelector } from '@/redux';
import { useFeature } from '@/hooks';
import { getPaginationMetaTag } from '@/utils/blogUtils';
import { getTwitterUsername, langOrFullLocale, sanitizeUrl } from '@/utils/millesimaUtils';
import { BLOG_PAGE_TYPE } from '@/constants/blog';
import { FEATURES } from '@/constants/features';
import { URLS } from '@/constants/urls';

export const MetaTagsBlog = ({ storeConf, type, content, slug, currentPageUrl }) => {
  const { blogResults } = useAppSelector(({ blog }) => blog);
  const [urlPrev, setUrlPrev] = useState(content?.articlesResult ?
    getPaginationMetaTag(content?.articlesResult, currentPageUrl).urlPrev : '');
  const [urlNext, setUrlNext] = useState(content?.articlesResult ?
    getPaginationMetaTag(content?.articlesResult, currentPageUrl).urlNext : '');

  const twitterUsername = getTwitterUsername(storeConf.twitter);
  const hreflangs = content.hrefLang;

  if (type === BLOG_PAGE_TYPE.BLOG_CATEG || type === BLOG_PAGE_TYPE.BLOG_HOME) {
    content = content.articleCateg.content[0];
  } else if (type === BLOG_PAGE_TYPE.BLOG_ARTICLE) {
    content = content.articleAggregation.articles[0];
  } else if (type === BLOG_PAGE_TYPE.BLOG_TAG) {
    content = content.tag.content[0];
  } else if (type === BLOG_PAGE_TYPE.BLOG_ARCHIVE) {
    content.urlKeyword = `archives/${slug}`;
    content.metaTitle = 'Archive ' + content.title;
    content.metaDescription = 'Archive ' + content.title;
    content.pageDesc = 'Archive ' + content.title;
  }

  const pageDesc = content.metaDescription;
  const urlKeyword = content.urlKeyword;
  const canonical = urlKeyword ? (urlKeyword === 'index.html' ?
    sanitizeUrl(storeConf.url + 'blog') :
    sanitizeUrl(storeConf.url + 'blog/', urlKeyword)) :
    '';

  const isSuffixEnabled = useFeature(storeConf, FEATURES.ADD_SUFFIX_META);
  const titleSuffix = isSuffixEnabled ? ` - ${storeConf?.shortUrl ?? 'Millesima'}` : '';
  const title = content.metaTitle + titleSuffix;

  let metaTags = {};

  useEffect(() => {
    if (blogResults?.content && blogResults?.pageable) {
      metaTags = getPaginationMetaTag(blogResults, currentPageUrl);
      setUrlPrev(metaTags.urlPrev);
      setUrlNext(metaTags.urlNext);
    }
  }, [blogResults]);

  return (
    <Head>
      <title>{title}</title>
      <meta name="type" content={type} />
      {pageDesc && <meta name="description" content={pageDesc} />}
      {twitterUsername &&
        <>
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content={twitterUsername} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={pageDesc} />
          <meta name="twitter:image" content={URLS.DEFAULT_OG_IMAGE} />
        </>
      }
      {urlKeyword &&
        <>
          <meta property="og:title" content={title} />
          <meta property="og:url" content={canonical} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:type" content="product" />
          <meta property="og:image" content={URLS.DEFAULT_OG_IMAGE} />
          <link rel="canonical" href={canonical} />
          <link rel="alternate" href={canonical} hrefLang={langOrFullLocale(storeConf.locale)} />
        </>
      }
      {hreflangs?.map((href) => (
        <link key={href.lng} rel="alternate" href={href.url} hrefLang={langOrFullLocale(href.lng)} />
      ))}
      {urlPrev && <link rel="prev" href={urlPrev} />}
      {urlNext && <link rel="next" href={urlNext} />}
    </Head>
  );
};

MetaTagsBlog.propTypes = {
  storeConf: PropTypes.object,
  type: PropTypes.oneOf(Object.values(BLOG_PAGE_TYPE)).isRequired,
  content: PropTypes.object,
  slug: PropTypes.string,
  currentPageUrl: PropTypes.string
};

export default MetaTagsBlog;
